/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { forwardRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';
import { HexColorPicker } from 'react-colorful';

import TextInput from './textInput';

import styles from '../styles/uikitInputsColor.module.scss';
import {
  isColorNearlyWhite,
  isValidHexValue,
  useEscKeyHandler,
  useOnClickOutside,
} from '../../utils';

const ColorInput = forwardRef(
  (
    {
      ariaLabel = '',
      buttonAriaLabel = '',
      className = '',
      id,
      isDisabled = false,
      isReadOnly = false,
      name = '',
      onChange = () => {},
      placeholderColor = '',
      placeholderInput = i18n.t('default', 'Default'),
      testId = '',
      value = '',
    },
    ref,
  ) => {
    const colorInputRef = React.useRef(null);
    const buttonRef = React.useRef(null);
    const colorPickerAnnouncementRef = React.useRef(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const handleButtonClick = () => {
      if (isPickerOpen) {
        if (colorPickerAnnouncementRef.current) {
          colorPickerAnnouncementRef.current.textContent = '';
        }
        buttonRef.current.focus();
      }
      setIsPickerOpen(!isPickerOpen);
    };

    const handleChange = (newValue) => {
      onChange(newValue);
    };

    const handlePickerChange = (newValue) => {
      if (colorPickerAnnouncementRef.current) {
        colorPickerAnnouncementRef.current.textContent = i18n.t('color-changed', {
          color: newValue,
          defaultValue: `Color changed to ${newValue}`,
        });
      }
      handleChange(newValue);
    };

    useEscKeyHandler(handleButtonClick, isPickerOpen);
    useOnClickOutside({ ref: colorInputRef, callback: () => setIsPickerOpen(false) });

    const validHexColorOrEmpty = useMemo(() => (isValidHexValue(value) ? value : ''), [value]);
    const validDefaultColorOrEmpty = useMemo(
      () => (isValidHexValue(placeholderColor) ? placeholderColor : ''),
      [placeholderColor],
    );
    const isNearlyWhite = useMemo(
      () => isColorNearlyWhite(validHexColorOrEmpty || validDefaultColorOrEmpty),
      [validHexColorOrEmpty, validDefaultColorOrEmpty],
    );
    const swatchColorStyle = useMemo(() => {
      if (validHexColorOrEmpty) {
        return { backgroundColor: validHexColorOrEmpty };
      }
      if (value) {
        return {};
      }
      return { backgroundColor: validDefaultColorOrEmpty };
    }, [value, validHexColorOrEmpty, validDefaultColorOrEmpty]);

    return (
      <div className={classnames(styles.element, className)} data-testid={testId}>
        <div className={styles.wrapper} ref={colorInputRef}>
          <button
            {...(isDisabled || isReadOnly
              ? { 'aria-disabled': true }
              : { onClick: handleButtonClick })}
            aria-label={buttonAriaLabel || i18n.t('choose-color', 'Choose color')}
            className={classnames(styles.swatchButton, {
              [styles.isDisabled]: isDisabled || isReadOnly,
            })}
            data-testid={`${testId}-swatch`}
            ref={buttonRef}
            type="button"
          >
            <div
              className={classnames(styles.swatch, {
                [styles.isNearlyWhite]: isNearlyWhite,
                [styles.isInvalid]: !validHexColorOrEmpty && value,
              })}
              style={swatchColorStyle}
            />
          </button>
          {isPickerOpen && (
            <div className={styles.colorPicker} data-testid={`${testId}-color-picker`}>
              <HexColorPicker
                color={validHexColorOrEmpty || validDefaultColorOrEmpty || '#fff'}
                onChange={handlePickerChange}
              />
            </div>
          )}
          <div
            className={styles.colorPickerAnnouncement}
            aria-live="polite"
            aria-atomic="true"
            ref={colorPickerAnnouncementRef}
          />
          <TextInput
            aria-label={ariaLabel}
            className={styles.input}
            id={id}
            inputTestId={`${testId}-input`}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            name={name}
            onChange={handleChange}
            placeholder={placeholderInput}
            ref={ref}
            value={value}
          />
        </div>
      </div>
    );
  },
);

ColorInput.propTypes = {
  ariaLabel: PropTypes.string,
  buttonAriaLabel: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholderColor: PropTypes.string,
  placeholderInput: PropTypes.string,
  testId: PropTypes.string,
  value: PropTypes.string,
};

export { ColorInput as ColorInputStorybook };
export default React.memo(ColorInput);
